import { Text } from '@blocksuite/affine/store';

import type { DocsService } from '../doc';

export type OrgTemplateKey =
  // свадьба
  | 'hub'
  | 'budget'
  | 'timeline'
  | 'day'
  | 'checklist'
  | 'bookings'
  | 'venue'
  | 'honeymoon'
  // люди
  | 'guests'
  | 'seating'
  | 'team'
  | 'contacts'
  | 'crm'
  // для пары
  | 'registry'
  | 'brief'
  | 'website'
  // бизнес
  | 'projects'
  | 'content'
  | 'meeting'
  | 'finance'
  | 'invoices'
  | 'knowledge'
  // личное
  | 'goals'
  | 'habits'
  | 'reading'
  | 'meals';

export type TemplateGanttScale =
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year';

interface ColumnSpec {
  name: string;
  type: string;
  data?: Record<string, unknown>;
}

export interface TemplateSectionSpec {
  heading: string;
  prompts: string[];
}

/** One table of a multi-part page (Notion-style pages carry several). */
export interface TemplateTableBlock {
  kind: 'table';
  /** h2 above the database; the database itself carries the same title. */
  title: string;
  columns: ColumnSpec[];
  rows?: TemplateRowSpec[];
  ganttScale?: TemplateGanttScale;
  ganttFirst?: boolean;
  anchorHour?: number;
}

/**
 * Page building blocks. Most templates are one table (legacy fields below),
 * but the Notion-style pages — hub, протокол встречи, меню — mix headings,
 * prompts, to-dos and several databases on one page.
 */
export type TemplateBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'text'; text: string }
  | { kind: 'todo'; items: string[] }
  | TemplateTableBlock;

export interface TemplateRowSpec {
  title?: string;
  /**
   * Days offset from creation time. The seeded values are demo dates so the
   * table and «Инфокарта» are not empty on first open — shift them as needed.
   */
  dateRange?: [number, number];
  /** Hours offset from `anchorHour` (fractions allowed) — intraday schedules. */
  hours?: [number, number];
}

export interface TemplateSpec {
  key: OrgTemplateKey;
  title: string;
  columns?: ColumnSpec[];
  /**
   * Seeded rows so the table and «Инфокарта» are not empty on first open.
   * Dates are applied through the data-view API after row blocks exist — the
   * raw `cells` prop does not survive for date-range columns.
   */
  rows?: TemplateRowSpec[];
  /** Doc-only template: h2 headings with prompt paragraphs, no database. */
  sections?: TemplateSectionSpec[];
  /** Multi-part page; when set, `columns`/`rows`/`sections` are ignored. */
  blocks?: TemplateBlock[];
  /** «Инфокарта» granularity for templates with a date column. */
  ganttScale?: TemplateGanttScale;
  /** Put the gantt view first in the tab bar (schedules read as charts). */
  ganttFirst?: boolean;
  /** Local hour the `hours` offsets are counted from. */
  anchorHour?: number;
}

/**
 * Select options as the property model stores them: `id` doubles as the cell
 * value, colour is a tag variable so it follows the theme.
 */
const options = (...items: [label: string, color: string][]) => ({
  options: items.map(([value, color]) => ({ id: value, value, color })),
});

const TAG = {
  green: 'var(--affine-tag-green)',
  red: 'var(--affine-tag-red)',
  orange: 'var(--affine-tag-orange)',
  yellow: 'var(--affine-tag-yellow)',
  blue: 'var(--affine-tag-blue)',
  teal: 'var(--affine-tag-teal)',
  pink: 'var(--affine-tag-pink)',
  purple: 'var(--affine-tag-purple)',
  gray: 'var(--affine-tag-gray)',
};

/**
 * Ready-made page structures the organizer reaches for on every wedding:
 * смета, тайминг, команда and the rest of the gallery. They are ordinary docs
 * with a database block, so the organizer can copy them or mark one as a
 * template.
 */
export const ORG_TEMPLATES: Record<OrgTemplateKey, TemplateSpec> = {
  budget: {
    key: 'budget',
    title: 'Смета',
    rows: [{ title: 'Площадка' }, { title: 'Кейтеринг' }, { title: 'Декор' }],
    columns: [
      { name: 'Позиция', type: 'title' },
      { name: 'Категория', type: 'text' },
      {
        name: 'План',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      {
        name: 'Факт',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      {
        name: 'Оплачено',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
    ],
  },
  timeline: {
    key: 'timeline',
    title: 'Тайминг подготовки',
    ganttFirst: true,
    rows: [
      { title: 'Подготовка', dateRange: [-7, 7] },
      { title: 'Согласование сметы', dateRange: [0, 10] },
      { title: 'Утверждение площадки', dateRange: [2, 6] },
      { title: 'Депозит площадки', dateRange: [6, 7] },
      { title: 'Выбор фотографа', dateRange: [3, 9] },
      { title: 'Договор с фотографом', dateRange: [9, 10] },
      { title: 'Лав-стори', dateRange: [12, 12] },
      { title: 'Выбор видеографа', dateRange: [4, 11] },
      { title: 'Договор с видеографом', dateRange: [11, 12] },
      { title: 'Дегустация меню', dateRange: [10, 14] },
      { title: 'Утверждение меню', dateRange: [14, 15] },
      { title: 'Рассылка приглашений', dateRange: [8, 20] },
      { title: 'Сбор ответов гостей', dateRange: [20, 30] },
      { title: 'Флористика: концепция', dateRange: [12, 18] },
      { title: 'Заказ цветов', dateRange: [25, 28] },
      { title: 'Дегустация торта', dateRange: [16, 18] },
      { title: 'Заказ торта', dateRange: [18, 19] },
      { title: 'Диджей: выбор', dateRange: [15, 22] },
      { title: 'Договор с диджеем', dateRange: [22, 23] },
      { title: 'Аренда платья', dateRange: [14, 21] },
      { title: 'Примерка платья', dateRange: [24, 24] },
      { title: 'Костюм жениха', dateRange: [16, 23] },
      { title: 'Покупка колец', dateRange: [18, 25] },
      { title: 'Визажист: репетиция', dateRange: [26, 26] },
      { title: 'Трансфер гостей', dateRange: [30, 32] },
      { title: 'Сценарий вечера', dateRange: [28, 34] },
      { title: 'Репетиция церемонии', dateRange: [34, 34] },
      { title: 'Финальная сверка сметы', dateRange: [35, 36] },
      { title: 'День свадьбы', dateRange: [36, 38] },
      { title: 'Отъезд / медовый месяц', dateRange: [39, 45] },
    ],
    columns: [
      { name: 'Этап', type: 'title' },
      // a date-range column carries both ends, so «Инфокарта» draws one bar per row
      { name: 'Срок', type: 'date-range' },
      { name: 'Ответственный', type: 'text' },
      { name: 'Комментарий', type: 'text' },
    ],
  },
  day: {
    key: 'day',
    title: 'Сценарий дня',
    ganttScale: 'hour',
    ganttFirst: true,
    anchorHour: 8,
    rows: [
      { title: 'Сборы невесты', hours: [1, 3] },
      { title: 'Сборы жениха', hours: [1.5, 3] },
      { title: 'Первая встреча', hours: [3.5, 4] },
      { title: 'Фотопрогулка', hours: [4, 6] },
      { title: 'Выездная церемония', hours: [7, 8] },
      { title: 'Фуршет', hours: [8, 9] },
      { title: 'Банкет', hours: [9, 14] },
      { title: 'Первый танец', hours: [10, 10.5] },
      { title: 'Торт', hours: [12, 12.5] },
      { title: 'Фейерверк', hours: [13.5, 14] },
    ],
    columns: [
      { name: 'Блок', type: 'title' },
      { name: 'Время', type: 'date-range' },
      { name: 'Ответственный', type: 'text' },
      { name: 'Где', type: 'text' },
    ],
  },
  checklist: {
    key: 'checklist',
    title: 'Чек-лист подготовки',
    rows: [
      { title: 'Подать заявление в ЗАГС' },
      { title: 'Забронировать площадку' },
      { title: 'Выбрать фотографа' },
      { title: 'Согласовать меню' },
      { title: 'Отправить приглашения' },
      { title: 'Продумать рассадку' },
    ],
    columns: [
      { name: 'Дело', type: 'title' },
      { name: 'Готово', type: 'checkbox' },
      { name: 'Срок', type: 'date' },
      { name: 'Ответственный', type: 'text' },
    ],
  },
  bookings: {
    key: 'bookings',
    title: 'Бронирования',
    rows: [
      { title: 'Ресторан «Веранда»' },
      { title: 'Отель для гостей' },
      { title: 'Трансфер' },
    ],
    columns: [
      { name: 'Объект', type: 'title' },
      {
        name: 'Тип',
        type: 'select',
        data: options(
          ['Площадка', TAG.blue],
          ['Отель', TAG.teal],
          ['Трансфер', TAG.purple],
          ['Фото/Видео', TAG.pink],
          ['Музыка', TAG.orange],
          ['Декор', TAG.green]
        ),
      },
      { name: 'Дата', type: 'date' },
      {
        name: 'Статус',
        type: 'select',
        data: options(
          ['Запрошено', TAG.yellow],
          ['Забронировано', TAG.blue],
          ['Оплачено', TAG.green],
          ['Отменено', TAG.red]
        ),
      },
      {
        name: 'Сумма',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      { name: 'Контакт', type: 'text' },
    ],
  },
  guests: {
    key: 'guests',
    title: 'Гости и рассадка',
    rows: [
      { title: 'Анна и Пётр Сидоровы' },
      { title: 'Мария Иванова' },
      { title: 'Семья Ковалёвых' },
    ],
    columns: [
      { name: 'Гость', type: 'title' },
      {
        name: 'Сторона',
        type: 'select',
        data: options(
          ['Жених', TAG.blue],
          ['Невеста', TAG.pink],
          ['Общие', TAG.teal]
        ),
      },
      {
        name: 'Ответ',
        type: 'select',
        data: options(
          ['Придёт', TAG.green],
          ['Не придёт', TAG.red],
          ['Не ответил', TAG.orange]
        ),
      },
      { name: 'Стол', type: 'number', data: { decimal: 0 } },
      { name: 'Примечание', type: 'text' },
    ],
  },
  team: {
    key: 'team',
    title: 'Команда',
    rows: [{ title: 'Ведущий' }, { title: 'Фотограф' }],
    columns: [
      { name: 'Имя', type: 'title' },
      { name: 'Роль', type: 'text' },
      { name: 'Контакт', type: 'text' },
      {
        name: 'Ставка',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
    ],
  },
  contacts: {
    key: 'contacts',
    title: 'Подрядчики',
    rows: [
      { title: 'Площадка «Веранда»' },
      { title: 'Фотограф А. Смирнов' },
      { title: 'Флорист М. Ким' },
    ],
    columns: [
      { name: 'Название', type: 'title' },
      { name: 'Роль', type: 'text' },
      { name: 'Телефон', type: 'text' },
      { name: 'Ссылка', type: 'text' },
      {
        name: 'Цена',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
    ],
  },
  brief: {
    key: 'brief',
    title: 'Бриф пары',
    sections: [
      {
        heading: 'О паре',
        prompts: [
          'Имена, как познакомились, дата и место свадьбы.',
          'Сколько гостей планируется?',
          'Что для вас самое главное в этот день?',
        ],
      },
      {
        heading: 'Стиль и атмосфера',
        prompts: [
          'Три слова, которыми вы описываете свою свадьбу.',
          'Любимые цвета — и что точно не нравится.',
          'Примеры, которые понравились (пришлите ссылки).',
        ],
      },
      {
        heading: 'Бюджет',
        prompts: [
          'Ориентир по бюджету.',
          'На чём экономить нельзя.',
          'Что можно упростить без сожаления.',
        ],
      },
      {
        heading: 'Особые пожелания',
        prompts: [
          'Традиции и ритуалы, которые важны для семей.',
          'Что точно должно случиться в этот день.',
          'Есть ли сюрпризы для второго партнёра?',
        ],
      },
    ],
  },
  // ——— свадьба: по мотивам топовых шаблонов Notion ———
  hub: {
    key: 'hub',
    title: 'Свадьба: центр планирования',
    blocks: [
      {
        kind: 'text',
        text: 'Всё в одном месте: чек-лист по фазам, бюджет, гости и подрядчики. Дата свадьбы: ______ · До свадьбы осталось: ______',
      },
      { kind: 'heading', text: 'Чек-лист по фазам' },
      {
        kind: 'todo',
        items: [
          'За 12 месяцев — определить бюджет и число гостей',
          'За 12 месяцев — выбрать и забронировать площадку',
          'За 9 месяцев — найти фотографа и ведущего',
          'За 6 месяцев — отправить save-the-date',
          'За 3 месяца — согласовать меню и программу',
          'За 1 месяц — разослать приглашения',
          'За неделю — финальная сверка с подрядчиками',
        ],
      },
      {
        kind: 'table',
        title: 'Бюджет',
        columns: [
          { name: 'Статья', type: 'title' },
          {
            name: 'План',
            type: 'number',
            data: { decimal: 0, format: 'currencyRUB' },
          },
          {
            name: 'Факт',
            type: 'number',
            data: { decimal: 0, format: 'currencyRUB' },
          },
        ],
        rows: [
          { title: 'Площадка' },
          { title: 'Кейтеринг' },
          { title: 'Фото и видео' },
        ],
      },
      {
        kind: 'table',
        title: 'Гости',
        columns: [
          { name: 'Гость', type: 'title' },
          {
            name: 'Ответ',
            type: 'select',
            data: options(
              ['Придёт', TAG.green],
              ['Не придёт', TAG.red],
              ['Ждём ответа', TAG.orange]
            ),
          },
          { name: 'Стол', type: 'number', data: { decimal: 0 } },
        ],
        rows: [{ title: 'Анна и Пётр Сидоровы' }, { title: 'Мария Иванова' }],
      },
      {
        kind: 'table',
        title: 'Подрядчики',
        columns: [
          { name: 'Название', type: 'title' },
          { name: 'Роль', type: 'text' },
          {
            name: 'Статус',
            type: 'select',
            data: options(
              ['Ищем', TAG.yellow],
              ['На встрече', TAG.blue],
              ['Договор подписан', TAG.green]
            ),
          },
          { name: 'Контакт', type: 'text' },
        ],
        rows: [
          { title: 'Площадка «Веранда»' },
          { title: 'Фотограф А. Смирнов' },
        ],
      },
    ],
  },
  venue: {
    key: 'venue',
    title: 'Подбор площадки',
    rows: [
      { title: 'Ресторан «Веранда»' },
      { title: 'Усадьба «Сосны»' },
      { title: 'Лофт «Депо»' },
    ],
    columns: [
      { name: 'Площадка', type: 'title' },
      {
        name: 'Аренда',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      {
        name: 'Цена за гостя',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      {
        name: 'Мин. депозит',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      {
        name: 'Статус',
        type: 'select',
        data: options(
          ['Запрос отправлен', TAG.yellow],
          ['Просмотр', TAG.blue],
          ['Сравнение', TAG.purple],
          ['Забронировано', TAG.green],
          ['Отказ', TAG.red]
        ),
      },
      { name: 'Вопросы на просмотре', type: 'text' },
    ],
  },
  honeymoon: {
    key: 'honeymoon',
    title: 'Медовый месяц',
    rows: [
      { title: 'Выбрать направление', dateRange: [7, 14] },
      { title: 'Купить билеты', dateRange: [14, 18] },
      { title: 'Забронировать отель', dateRange: [18, 24] },
      { title: 'Собрать чемоданы', dateRange: [35, 37] },
    ],
    columns: [
      { name: 'Шаг', type: 'title' },
      { name: 'Срок', type: 'date-range' },
      {
        name: 'Бюджет',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      {
        name: 'Статус',
        type: 'select',
        data: options(
          ['Идея', TAG.yellow],
          ['В работе', TAG.blue],
          ['Готово', TAG.green]
        ),
      },
      { name: 'Примечание', type: 'text' },
    ],
  },
  // ——— люди ———
  seating: {
    key: 'seating',
    title: 'План столов',
    rows: [
      { title: 'Стол 1 — близкие' },
      { title: 'Стол 2 — друзья' },
      { title: 'Стол 3 — коллеги' },
    ],
    columns: [
      { name: 'Стол', type: 'title' },
      { name: 'Вместимость', type: 'number', data: { decimal: 0 } },
      { name: 'Гости', type: 'text' },
      { name: 'Заметки', type: 'text' },
    ],
  },
  crm: {
    key: 'crm',
    title: 'CRM: пары и заявки',
    rows: [
      { title: 'Аня и Вася', dateRange: [30, 30] },
      { title: 'Катя и Рома', dateRange: [90, 90] },
      { title: 'Лена и Максим', dateRange: [150, 150] },
    ],
    columns: [
      { name: 'Пара', type: 'title' },
      { name: 'Дата свадьбы', type: 'date-range' },
      {
        name: 'Статус',
        type: 'select',
        data: options(
          ['Лид', TAG.gray],
          ['Встреча', TAG.yellow],
          ['Договор подписан', TAG.blue],
          ['В работе', TAG.purple],
          ['Завершено', TAG.green],
          ['Отказ', TAG.red]
        ),
      },
      {
        name: 'Бюджет',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      { name: 'Контакт', type: 'text' },
      { name: 'Следующий шаг', type: 'text' },
    ],
  },
  // ——— для пары ———
  registry: {
    key: 'registry',
    title: 'Список подарков',
    rows: [
      { title: 'Сервиз для чая' },
      { title: 'Путешествие' },
      { title: 'Кофемашина' },
      { title: 'Картина для гостиной' },
    ],
    columns: [
      { name: 'Подарок', type: 'title' },
      { name: 'Категория', type: 'text' },
      { name: 'Где купить', type: 'text' },
      {
        name: 'Цена',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      {
        name: 'Статус',
        type: 'select',
        data: options(
          ['Хочу', TAG.yellow],
          ['Забронирован', TAG.blue],
          ['Подарен', TAG.green]
        ),
      },
    ],
  },
  website: {
    key: 'website',
    title: 'Страница для гостей',
    sections: [
      {
        heading: 'Аня и Вася',
        prompts: [
          'Мы женимся! Дата: ______ · Место: ______',
          'Расскажите гостям, как вы познакомились — пара абзацев тёплого текста.',
        ],
      },
      {
        heading: 'Программа дня',
        prompts: [
          '15:00 — сбор гостей · 16:00 — церемония · 17:00 — фуршет · 18:00 — банкет',
          'Опишите дресс-код и что взять с собой.',
        ],
      },
      {
        heading: 'Как добраться',
        prompts: [
          'Адрес и ориентиры, время работы трансфера.',
          'Ссылка на карту: ______',
        ],
      },
      {
        heading: 'Где остановиться',
        prompts: [
          'Отели рядом со скидкой для гостей — название, телефон, цена.',
        ],
      },
      {
        heading: 'Пожелания',
        prompts: [
          'Вместо подарков — ______ (ссылка на список подарков или конверт).',
          'Форма ответа на приглашение (RSVP): ______',
        ],
      },
    ],
  },
  // ——— бизнес ———
  projects: {
    key: 'projects',
    title: 'Проекты и задачи',
    rows: [
      { title: 'Подготовить презентацию для пары' },
      { title: 'Согласовать смету с площадкой' },
      { title: 'Отправить договор фотографу' },
    ],
    columns: [
      { name: 'Задача', type: 'title' },
      {
        name: 'Статус',
        type: 'select',
        data: options(
          ['К выполнению', TAG.gray],
          ['В работе', TAG.blue],
          ['На паузе', TAG.yellow],
          ['Готово', TAG.green]
        ),
      },
      {
        name: 'Приоритет',
        type: 'select',
        data: options(
          ['Низкий', TAG.teal],
          ['Средний', TAG.blue],
          ['Высокий', TAG.orange],
          ['Срочно', TAG.red]
        ),
      },
      { name: 'Срок', type: 'date' },
      { name: 'Исполнитель', type: 'text' },
    ],
  },
  content: {
    key: 'content',
    title: 'Контент-план',
    rows: [
      { title: 'Кейс: свадьба в усадьбе «Сосны»' },
      { title: 'Гид по выбору площадки' },
      { title: 'Отзыв пары Ани и Васи' },
    ],
    columns: [
      { name: 'Материал', type: 'title' },
      {
        name: 'Площадка',
        type: 'select',
        data: options(
          ['Telegram', TAG.blue],
          ['Instagram', TAG.pink],
          ['ВКонтакте', TAG.teal],
          ['Сайт', TAG.purple]
        ),
      },
      {
        name: 'Формат',
        type: 'select',
        data: options(
          ['История', TAG.yellow],
          ['Пост', TAG.blue],
          ['Видео', TAG.pink],
          ['Статья', TAG.teal]
        ),
      },
      { name: 'Дата', type: 'date' },
      {
        name: 'Статус',
        type: 'select',
        data: options(
          ['Идея', TAG.gray],
          ['Черновик', TAG.yellow],
          ['На согласовании', TAG.blue],
          ['Опубликован', TAG.green]
        ),
      },
    ],
  },
  meeting: {
    key: 'meeting',
    title: 'Протокол встречи',
    blocks: [
      {
        kind: 'text',
        text: 'Дата: ______ · Участники: ______ · Повестка: ______',
      },
      { kind: 'heading', text: 'Решения' },
      {
        kind: 'text',
        text: 'Что договорились сделать — фиксируйте сразу, а потом переносите в задачи.',
      },
      {
        kind: 'table',
        title: 'Договорённости',
        columns: [
          { name: 'Что решили', type: 'title' },
          { name: 'Кто', type: 'text' },
          { name: 'Когда', type: 'date' },
          {
            name: 'Статус',
            type: 'select',
            data: options(
              ['Открыто', TAG.gray],
              ['В работе', TAG.blue],
              ['Сделано', TAG.green]
            ),
          },
        ],
        rows: [
          { title: 'Согласовать смету' },
          { title: 'Уточнить количество гостей' },
        ],
      },
    ],
  },
  finance: {
    key: 'finance',
    title: 'Финансы студии',
    rows: [
      { title: 'Предоплата — свадьба Ани и Васи' },
      { title: 'Аренда офиса' },
      { title: 'Реклама в Telegram' },
    ],
    columns: [
      { name: 'Операция', type: 'title' },
      {
        name: 'Тип',
        type: 'select',
        data: options(['Доход', TAG.green], ['Расход', TAG.red]),
      },
      { name: 'Категория', type: 'text' },
      {
        name: 'Сумма',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      { name: 'Дата', type: 'date' },
    ],
  },
  invoices: {
    key: 'invoices',
    title: 'Счета и оплаты',
    rows: [
      { title: 'Счёт № 1 — Аня и Вася' },
      { title: 'Счёт № 2 — Катя и Рома' },
    ],
    columns: [
      { name: 'Счёт', type: 'title' },
      { name: 'Клиент', type: 'text' },
      {
        name: 'Сумма',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      { name: 'Выставлен', type: 'date' },
      { name: 'Оплачен', type: 'checkbox' },
      {
        name: 'Статус',
        type: 'select',
        data: options(
          ['Не оплачен', TAG.red],
          ['Частично', TAG.yellow],
          ['Оплачен', TAG.green],
          ['Просрочен', TAG.orange]
        ),
      },
    ],
  },
  knowledge: {
    key: 'knowledge',
    title: 'База знаний студии',
    rows: [
      { title: 'Как проходит первая встреча с парой' },
      { title: 'Чек-лист договора с подрядчиком' },
      { title: 'Регламент работы в день свадьбы' },
    ],
    columns: [
      { name: 'Процесс', type: 'title' },
      {
        name: 'Раздел',
        type: 'select',
        data: options(
          ['Продажи', TAG.blue],
          ['Производство', TAG.orange],
          ['Финансы', TAG.green],
          ['Клиенты', TAG.pink],
          ['Администрация', TAG.gray]
        ),
      },
      { name: 'Кто ведёт', type: 'text' },
      { name: 'Обновлено', type: 'date' },
      { name: 'Ссылка', type: 'text' },
    ],
  },
  // ——— личное ———
  goals: {
    key: 'goals',
    title: 'Цели и OKR',
    blocks: [
      { kind: 'heading', text: 'Цели года' },
      {
        kind: 'text',
        text: 'Одна большая цель — и три ключевых результата, по которым видно прогресс.',
      },
      {
        kind: 'table',
        title: 'OKR',
        columns: [
          { name: 'Цель', type: 'title' },
          { name: 'Ключевой результат', type: 'text' },
          { name: 'Прогресс, %', type: 'number', data: { decimal: 0 } },
          { name: 'Срок', type: 'date' },
          {
            name: 'Статус',
            type: 'select',
            data: options(
              ['В работе', TAG.blue],
              ['Достигнута', TAG.green],
              ['Отложена', TAG.gray]
            ),
          },
        ],
        rows: [
          { title: 'Провести 20 свадеб' },
          { title: 'Запустить сайт студии' },
        ],
      },
    ],
  },
  habits: {
    key: 'habits',
    title: 'Трекер привычек',
    rows: [
      { title: 'Утренняя зарядка' },
      { title: 'Прогулка 30 минут' },
      { title: 'Читать 20 страниц' },
      { title: 'Без соцсетей до обеда' },
    ],
    columns: [
      { name: 'Привычка', type: 'title' },
      { name: 'Пн', type: 'checkbox' },
      { name: 'Вт', type: 'checkbox' },
      { name: 'Ср', type: 'checkbox' },
      { name: 'Чт', type: 'checkbox' },
      { name: 'Пт', type: 'checkbox' },
      { name: 'Сб', type: 'checkbox' },
      { name: 'Вс', type: 'checkbox' },
      { name: 'Серия', type: 'number', data: { decimal: 0 } },
    ],
  },
  reading: {
    key: 'reading',
    title: 'Читательский список',
    rows: [
      { title: 'Не заставляйте меня думать' },
      { title: 'Эмоциональный интеллект' },
      { title: 'Искусство войны' },
    ],
    columns: [
      { name: 'Книга', type: 'title' },
      { name: 'Автор', type: 'text' },
      {
        name: 'Статус',
        type: 'select',
        data: options(
          ['Хочу читать', TAG.gray],
          ['Читаю', TAG.blue],
          ['Прочитано', TAG.green]
        ),
      },
      { name: 'Оценка', type: 'number', data: { decimal: 0 } },
      { name: 'Цитата', type: 'text' },
    ],
  },
  meals: {
    key: 'meals',
    title: 'Меню на неделю',
    blocks: [
      { kind: 'heading', text: 'Меню' },
      {
        kind: 'table',
        title: 'Меню',
        columns: [
          {
            name: 'День',
            type: 'select',
            data: options(
              ['Пн', TAG.blue],
              ['Вт', TAG.teal],
              ['Ср', TAG.green],
              ['Чт', TAG.yellow],
              ['Пт', TAG.orange],
              ['Сб', TAG.pink],
              ['Вс', TAG.purple]
            ),
          },
          {
            name: 'Приём пищи',
            type: 'select',
            data: options(
              ['Завтрак', TAG.yellow],
              ['Обед', TAG.orange],
              ['Ужин', TAG.purple]
            ),
          },
          { name: 'Блюдо', type: 'title' },
        ],
        rows: [
          { title: 'Овсяная каша с ягодами' },
          { title: 'Цезарь с курицей' },
          { title: 'Паста с морепродуктами' },
        ],
      },
      { kind: 'heading', text: 'Покупки' },
      {
        kind: 'table',
        title: 'Покупки',
        columns: [
          { name: 'Продукт', type: 'title' },
          { name: 'Категория', type: 'text' },
          { name: 'Куплено', type: 'checkbox' },
        ],
        rows: [
          { title: 'Овсянка' },
          { title: 'Куриное филе' },
          { title: 'Томаты' },
        ],
      },
    ],
  },
};

const newId = () => crypto.randomUUID();

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export async function createTemplateDoc(
  docsService: DocsService,
  key: OrgTemplateKey,
  title?: string
): Promise<string> {
  const spec = ORG_TEMPLATES[key];
  const docRecord = docsService.createDoc({ title: title ?? spec.title });

  const { doc, release } = docsService.open(docRecord.id);
  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    const note = store.getBlocksByFlavour('affine:note').at(0);
    if (!note) return docRecord.id;

    // Cells are written after the transaction commits: rows and their ids only
    // exist as committed blocks, and writing `cells` inside the same transact
    // left every date cell empty.
    const pending: {
      dbId: string;
      cells: Record<
        string,
        Record<string, { columnId: string; value: unknown }>
      >;
    }[] = [];

    /** h2 + database + seeded rows of one table block. */
    const addTable = (table: TemplateTableBlock) => {
      const columns = table.columns.map(column => ({
        id: newId(),
        type: column.type,
        name: column.name,
        data: column.data ?? {},
      }));
      const titleColumnId = columns.find(c => c.type === 'title')?.id;
      // date-range column → the gantt view can draw a bar per row
      const dateColumnId = columns.find(c => c.type === 'date-range')?.id;

      store.addBlock(
        'affine:paragraph',
        { type: 'h2', text: new Text(table.title) },
        note.id
      );

      const ganttView = dateColumnId
        ? {
            id: newId(),
            name: 'Инфокарта',
            mode: 'gantt',
            filter: { type: 'group', op: 'and', conditions: [] },
            date: { startColumnId: dateColumnId },
            header: { titleColumn: titleColumnId },
            scale: table.ganttScale ?? 'week',
          }
        : null;
      const tableView = {
        id: newId(),
        name: 'Таблица',
        mode: 'table',
        columns: columns.map(column => ({
          id: column.id,
          width: 200,
        })),
        filter: { type: 'group', op: 'and', conditions: [] },
        header: { titleColumn: titleColumnId, iconColumn: 'type' },
      };

      const dbId = store.addBlock(
        'affine:database',
        {
          title: new Text(table.title),
          views: ganttView
            ? table.ganttFirst
              ? [ganttView, tableView]
              : [tableView, ganttView]
            : [tableView],
          columns,
          cells: {},
        },
        note.id
      );

      // Rows must exist before the cells map can reference them: `addBlock`
      // returns the real row id, a pre-generated one leaves the row empty.
      //
      // `anchorHour` shifts the demo dates of intraday schedules (сценарий
      // дня) so «Инфокарта» at hour scale shows the wedding day, not now().
      const base =
        table.anchorHour === undefined
          ? Date.now()
          : new Date().setHours(table.anchorHour, 0, 0, 0);
      const toMs = (value: number, intraday: boolean) =>
        base + (intraday ? value * HOUR_MS : value * DAY_MS);

      const cells: Record<
        string,
        Record<string, { columnId: string; value: unknown }>
      > = {};
      (table.rows ?? []).forEach(row => {
        const rowTitle = row.title ?? '';
        const rowId = store.addBlock(
          'affine:paragraph',
          { text: new Text(rowTitle) },
          dbId
        );
        const rowCells: Record<string, { columnId: string; value: unknown }> =
          {};
        if (rowTitle && titleColumnId) {
          rowCells[titleColumnId] = {
            columnId: titleColumnId,
            value: rowTitle,
          };
        }
        if (dateColumnId && (row.hours || row.dateRange)) {
          const intraday = !!row.hours;
          const range = row.hours ?? row.dateRange ?? [0, 0];
          rowCells[dateColumnId] = {
            columnId: dateColumnId,
            value: {
              start: toMs(range[0], intraday),
              end: toMs(range[1], intraday),
              hasTime: intraday,
            },
          };
        }
        if (Object.keys(rowCells).length) cells[rowId] = rowCells;
      });
      if (Object.keys(cells).length) pending.push({ dbId, cells });
    };

    store.transact(() => {
      if (spec.blocks) {
        // multi-part Notion-style page: headings, prompts, to-dos, tables
        for (const block of spec.blocks) {
          switch (block.kind) {
            case 'heading':
              store.addBlock(
                'affine:paragraph',
                { type: 'h2', text: new Text(block.text) },
                note.id
              );
              break;
            case 'text':
              store.addBlock(
                'affine:paragraph',
                { text: new Text(block.text) },
                note.id
              );
              break;
            case 'todo':
              for (const item of block.items) {
                store.addBlock(
                  'affine:list',
                  { type: 'todo', checked: false, text: new Text(item) },
                  note.id
                );
              }
              break;
            case 'table':
              addTable(block);
              break;
          }
        }
      } else if (spec.sections) {
        // doc-only template (бриф): headings with prompt paragraphs
        for (const section of spec.sections) {
          store.addBlock(
            'affine:paragraph',
            { type: 'h2', text: new Text(section.heading) },
            note.id
          );
          for (const prompt of section.prompts) {
            store.addBlock(
              'affine:paragraph',
              { text: new Text(prompt) },
              note.id
            );
          }
        }
      } else {
        addTable({
          kind: 'table',
          title: title ?? spec.title,
          columns: spec.columns ?? [],
          rows: spec.rows,
          ganttScale: spec.ganttScale,
          ganttFirst: spec.ganttFirst,
          anchorHour: spec.anchorHour,
        });
      }
    });

    for (const { dbId, cells } of pending) {
      store.updateBlock(dbId, { cells });
    }
  } finally {
    release();
  }

  return docRecord.id;
}
